import { Injectable } from '@angular/core';
import { MainService } from './main.service';
import { UsersService } from './users.service';
import { environment } from 'src/environments/environment';
import { MatTableDataSource } from '@angular/material/table';

@Injectable({
  providedIn: 'root'
})
export class ChangeRequestsService {

  constructor(
    private main: MainService,
    private usrService: UsersService
  ) { }
  areaName: string = '/change-requests';

  /**
   * Obtiene una lista de solicitudes de cambio.
   * @param filters - Objeto con { status, badges } para filtrar.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success" | "No valid solicitudes were found in the request",
   *   data: {
   *     id: number,
   *     id_usuario: number,
   *     id_responsable: number,
   *     status: number,
   *     titulo: string,
   *     descripcion: string,
   *     badges: string,
   *     created_at: Date,
   *     estimated_date: Date,
   *     finish_date: Date,
   *     extra_data: string,
   *     deleted: number,
   *     nombre_creador: string,
   *     nombre_responsable: string
   *   }[]
   * }
   */
  getSolicitudes(filters: { status?: string, badges?: string }) {
    return this.main.postRequest(filters, this.areaName + '/show');
  }

  /**
   * Crea una nueva solicitud de cambio.
   * @param solicitudData - Objeto con { titulo, descripcion, badges }.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success" | "Could not create solicitud",
   *   id_solicitud?: number
   * }
   */
  createSolicitud(solicitudData: any) {
    return this.main.postRequest(solicitudData, this.areaName + "/create");
  }

  /**
   * Actualiza una solicitud existente (título, descripción).
   * @param solicitudData - Objeto con { id_solicitud, titulo, descripcion }.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success"
   * }
   */
  updateSolicitud(solicitudData: any) {
    return this.main.postRequest(solicitudData, this.areaName + "/update");
  }

  /**
   * Cambia el estatus de una solicitud.
   * @param statusData - Objeto con { id_solicitud, status, status_name (para el log) }.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success" | "Could not update solicitud status",
   *   id_solicitud?: number
   * }
   */
  setSolicitudStatus(statusData: any) {
    return this.main.postRequest(statusData, this.areaName + "/status");
  }

  /**
   * Asigna un responsable a una solicitud.
   * @param responsableData - Objeto con { id_solicitud, id_responsable, responsable (para el log) }.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success"
   * }
   */
  setResponsable(responsableData: any) {
    return this.main.postRequest(responsableData, this.areaName + "/responsable");
  }

  /**
   * Establece la fecha estimada de una solicitud.
   * @param dateData - Objeto con { id_solicitud, date }.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success"
   * }
   */
  setEstimatedDate(dateData: any) {
    return this.main.postRequest(dateData, this.areaName + "/estimated-date");
  }

  /**
   * Agrega un mensaje a una solicitud.
   * @param messageData - Objeto con { id_solicitud, message }.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success" | "Could not store message for solicitud",
   *   id_mensaje?: number
   * }
   */
  addMessage(messageData: any) {
    return this.main.postRequest(messageData, this.areaName + "/message");
  }

  /**
   * Obtiene el historial de cambios de una solicitud.
   * @param id_solicitud - El ID de la solicitud.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success" | "No changelog were found in the request",
   *   data: {
   *     name: string,
   *     content: string,
   *     created_at: Date,
   *     owner: number,
   *     dif_rol: number,
   *     id_rol: number,
   *     rol: string,
   *     type: string,
   *     seen: number,
   *     id_compania: number,
   *     company_name: string,
   *     imagen: string,
   *     namereg: string
   *   }[]
   * }
   */
  getChangelog(id_solicitud: number) {
    return this.main.getRequest(this.areaName + `/changelog/${id_solicitud}`);
  }

  /**
   * Obtiene la lista completa de badges disponibles.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success" | "No badges were found",
   *   data: {
   *     id: number,
   *     nombre: number, // Parece ser un typo en la BD, debería ser string
   *     created_at: Date,
   *     deleted: number
   *   }[]
   * }
   */
  deleteSolicitud(id_solicitud: number) {
    return this.main.getRequest(this.areaName + `/delete/${id_solicitud}`);
  }

  /**
   * Obtiene la lista completa de badges disponibles.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success" | "No badges were found",
   *   data: {
   *     id: number,
   *     nombre: number, // Parece ser un typo en la BD, debería ser string
   *     created_at: Date,
   *     deleted: number
   *   }[]
   * }
   */

  getBadges() {
    return this.main.getRequest(this.areaName + '/badges');
  }

  /**
   * Crea un nuevo badge.
   * @param badgeData - Objeto con { nombre }.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success" | "Could not create badge",
   *   id_badge: number,
   * }
   */
  createBadge(badgeData: any) {
    return this.main.postRequest(badgeData, this.areaName + '/badges/create');
  }

  /**
   * Actualiza los badges de una solicitud.
   * @param badgeData - Objeto con { id_solicitud, badges, new_badges (para el log) }.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success"
   * }
   */
  updateBadges(badgeData: any) {
    return this.main.postRequest(badgeData, this.areaName + '/badges/update');
  }

  /**
   * Sube una imagen adjunta a una solicitud.
   * @param formData - FormData que contiene el archivo (blob) y el id_solicitud.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success" | "Failure" | "No valid file were found in the request",
   *   data?: {
   *      ETag: string,
   *      Location: string, // URL de la imagen en el bucket
   *      key: string,
   *      Bucket: string
   *   }
   * }
   */
  uploadImage(formData: FormData) {
    return this.main.postRequestFormData(formData, this.areaName + "/image/upload");
  }

  /**
   * Obtiene los archivos adjuntos de una solicitud.
   * @param id_solicitud - El ID de la solicitud.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success",
   *   data: {
   *     id: number,
   *     id_solicitud: number,
   *     id_usuario: number,
   *     nombre: string,
   *     archivo: string, // URL del archivo
   *     created_at: Date,
   *     nombre_usuario
   *   }[]
   * }
   */
  getSolicitudFiles(id_solicitud: number){
    return this.main.postRequest({id_solicitud}, this.areaName + '/images');
  }

    /**
   * Establece la fecha estimada de una solicitud.
   * @param dateData - Objeto con { id_solicitud, date, status }.
   * @returns un Observable que resuelve a:
   * {
   *   message: "Success"
   * }
   */
  editSolicitud(editData: any){
    return this.main.postRequest(editData, this.areaName + '/edit');
  }


}